/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import type { ClickStatus } from '@coursera/event-pulse-types';

import Form from 'bundles/authentication-common/components/Form';
import Submit from 'bundles/authentication-common/components/Submit';
import EmailInput from 'bundles/authentication-common/components/inputs/EmailInput';
import type SSOError from 'bundles/authentication-common/components/sso/SSOError';
import { EMAIL_CONSTRAINT } from 'bundles/authentication-common/constants/constraints';
import useTrackClickButton from 'bundles/authentication-common/hooks/useTrackClickButton';
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/authentication';

type Props = {
  // Parent owns the full async sequence (force-SSO check, login-methods lookup, navigation);
  // the form just collects the email and delegates.
  onSubmit: (email: string) => Promise<void>;
  onError: (email: string) => void;
  resetSsoError?: () => void;
  ssoError?: SSOError;
  onEmailChange?: (email: string) => void;
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const styles = {
  form: css`
    display: grid;
    grid-gap: var(--cds-spacing-150);
    grid-template-columns: 1fr;
  `,
  submit: css`
    width: 100%;
    margin-top: var(--cds-spacing-50);
  `,
};

const UnifiedAuthEntryForm = ({
  onSubmit,
  onError,
  resetSsoError,
  ssoError,
  onEmailChange: onEnterpriseSsoEmailChange,
}: Props) => {
  const [isRunning, setIsRunning] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const { trackButton } = useTrackClickButton();

  useEffect(() => {
    if (ssoError?.response?.prefillEmail) {
      setEmailValue(ssoError.response.prefillEmail);
      onEnterpriseSsoEmailChange?.(ssoError.response.prefillEmail);
    }
  }, [ssoError?.response?.prefillEmail, onEnterpriseSsoEmailChange]);

  const handleTracking = (status: ClickStatus) => {
    trackButton({ name: 'authentication_continue', clickStatus: status });
  };

  const handleFormSubmit = async (values: Record<string, string>): Promise<void | boolean | undefined> => {
    if (!values.email) {
      return false;
    }

    setIsRunning(true);

    try {
      await onSubmit(values.email);
      handleTracking('valid');
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: error?.responseJSON?.msg,
        },
      });
      handleTracking('invalid');

      return onError(values.email);
    } finally {
      setIsRunning(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetSsoError?.();
    const email = e.target.value;
    setEmailValue(email);
    onEnterpriseSsoEmailChange?.(email);
  };

  const handleOnInvalid = () => {
    handleTracking('invalid');
  };

  const constraints = {
    email: EMAIL_CONSTRAINT,
  };

  return (
    <Form
      css={styles.form}
      constraints={constraints}
      name="unified_auth_entry"
      onSubmit={handleFormSubmit}
      onInvalid={handleOnInvalid}
    >
      <EmailInput value={emailValue} onChange={handleEmailChange} autoFocus />
      <TrackedSubmit css={styles.submit} disabled={isRunning} trackingName="unified_auth_entry_form_submit_button">
        {_t('Continue')}
      </TrackedSubmit>
    </Form>
  );
};

export default UnifiedAuthEntryForm;
